import { TermsAcknowlegdementCard } from "@/components/footer";
import { i18nComponents } from "@/components/i18n";
import { RestrictedJurisdictionsBanner } from "@/components/legal.banners";
import { BannerCard, BannerTitle } from "@/components/ui/banners";
import { seo } from "@/lib/seo";
import { cn } from "@bidbattles/bloks/lib/ui";
import {
	faBan,
	faHandshake,
	faInfoCircle,
	faTag,
	faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/account-types")({
	component: RouteComponent,
	head: () => ({
		meta: [
			...seo({
				title: "Account Types | BidBattles",
			}),
		],
	}),
});

function RouteComponent() {
	const accountTypes = [
		{
			icon: <FontAwesomeIcon icon={faTag} />,
			name: "Free Account",
			description: (
				<>
					<p>
						Register a Free Account to be able to join all battles on the
						BidBattles platform.
					</p>
					<p>
						Earn BidBattle Loyalty Coins (BLC) and use them wisely to buy Bids
						in any new Battle. Remember: After a battle is completed{" "}
						<i18nComponents.Strong primary>
							any unused Bids get burnt
						</i18nComponents.Strong>
						.
					</p>
					<BannerCard className="text-gray-700">
						<BannerTitle className="text-gray-900">
							<FontAwesomeIcon
								icon={faInfoCircle}
								className="text-accent-primary"
							/>
							By Continuing with a Free Account , You Agree To:
						</BannerTitle>
						<ul className="list-disc ms-4 flex flex-col gap-2">
							<li>Additional shipping cost varies by sort of product.</li>
							<li>Unused purchased bids get burnt after a completed battle.</li>
						</ul>
					</BannerCard>
				</>
			),
		},
		{
			icon: <FontAwesomeIcon icon={faTrophy} />,
			name: "Premium Account",
			description: (
				<>
					<p>
						Premium Accounts is a monthly subscription plan. Earn more BLC
						weekly as a premium member in tasks or{" "}
						<i18nComponents.Strong primary>
							receiving all your unused purchased bids in BLC
						</i18nComponents.Strong>{" "}
						after a battle ends. This way you dont loose any momentum or
						strenght in your chosen battles. BLC never expires and can be used
						to pay for your bids in future battles. Register a Free Account to
						be able to join all battles on the BidBattles platform.
					</p>
					<p>
						Premium members dont pay any additional shipping costs for won
						products.
					</p>
					<BannerCard className="text-gray-700">
						<BannerTitle className="text-gray-900">
							<FontAwesomeIcon
								icon={faInfoCircle}
								className="text-accent-primary"
							/>
							By Continuing with a Premium Account , You Agree To:
						</BannerTitle>
						<ul className="list-disc ms-4 flex flex-col gap-2">
							<li>No additional shipping cost.</li>
							<li>Monthly subscription auto-renews.</li>
						</ul>
					</BannerCard>
				</>
			),
		},
		{
			icon: <FontAwesomeIcon icon={faHandshake} />,
			name: "Business Account",
			description: (
				<>
					<p>
						Business Account is a monthly based plan. As a entrepeneur you can
						create your own battles for any of your products. Earn the price you
						want for any product. Get more exposure for your webshop and more
						exitement around your business.
					</p>
					<p>
						BidBattles is a unique way to earn what a product is worth and at
						the same time receive more exitement. The more active your shop is
						the more BLC it earns. Use your BLC to create new Battles or pay for
						bids in other businesses their battles.
					</p>
					<BannerCard className="text-gray-700">
						<BannerTitle className="text-gray-900">
							<FontAwesomeIcon
								icon={faInfoCircle}
								className="text-accent-primary"
							/>
							By Continuing with a Business Account , You Agree To:
						</BannerTitle>
						<ul className="list-disc ms-4 flex flex-col gap-2">
							<li>Not participating in your own created Battles.</li>
							<li>Sending real products that you advertise with.</li>
							<li>Having a registered business in the European Union.</li>
							<li>You having a valid Tax number.</li>
						</ul>
					</BannerCard>
				</>
			),
		},
	];
	return (
		<main className="flex flex-col gap-y-8 items-stretch grow h-full">
			<section className="p-box flex flex-col items-center gap-y-12 md:gap-y-16 mx-auto">
				<div className="flex flex-col gap-y-3 text-center max-w-100 md:max-w-180">
					<h1 className="text-gray-900 font-bold text-5xl leading-11 md:text-6xl md:leading-22.5">
						Account Types
					</h1>
					<p className="text-gray-500 text-lg leading-7">
						The difference between Free, Premium and Business Accounts
					</p>
				</div>
			</section>
			<section className="flex flex-col items-center bg-gray-warm-100 gap-12 py-box">
				<RestrictedJurisdictionsBanner className="max-w-3xl" />
				{accountTypes.map((accountType) => (
					<ATCard key={accountType.name} className="gap-4 text-gray-700">
						<div className="flex gap-4 items-start">
							<ATIcon>{accountType.icon}</ATIcon>
							<h2 className="font-bold text-2xl md:text-3xl text-gray-900">
								{accountType.name}
							</h2>
						</div>
						{accountType.description}
					</ATCard>
				))}
				<TermsAcknowlegdementCard />
			</section>
		</main>
	);
}

function ATCard({
	className,
	children,
}: React.PropsWithChildren<{ className?: string }>) {
	return (
		<div
			className={cn(
				"p-4 md:p-8 w-full max-w-3xl flex flex-col bg-white rounded-xl",
				className,
			)}
		>
			{children}
		</div>
	);
}

function ATIcon({
	className,
	children,
}: React.PropsWithChildren<{ className?: string }>) {
	return (
		<div
			className={cn(
				"rounded-full size-8 md:size-12 flex items-center justify-center",
				className,
			)}
			style={{
				background: "linear-gradient(135deg, #FF7B3C 0%, #FF8C42 100%)",
			}}
		>
			<p className="text-white text-2xl">{children}</p>
		</div>
	);
}
